import { action } from "typesafe-actions";

import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { ICustomerAllPolicyDetail, INotices } from "./notices.types";
import { makeApiCall } from "../../helpers/postRequest";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { uploadFile } from "../../features/FileUpload/utils";

export const FETCH_NOTICES_LIST_PROGRESS = "FETCH_NOTICES_LIST_PROGRESS";
export const FETCH_NOTICES_LIST_SUCCESS = "FETCH_NOTICES_LIST_SUCCESS";
export const FETCH_NOTICES_LIST_FAILED = "FETCH_NOTICES_LIST_FAILED";

export const fetchNoticesListProgress = () =>
  action(FETCH_NOTICES_LIST_PROGRESS);
export const fetchNoticesListSuccess = (
  list: INotices[],
  totalRecords: number
) => action(FETCH_NOTICES_LIST_SUCCESS, { list, totalRecords });
export const fetchNoticesListFailed = () => action(FETCH_NOTICES_LIST_FAILED);

export const fetchNoticesListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    type?: string,
    edocType?: string,
    actionState?: { action_needed: string; action_status: string },
    otherFilters?: {
      customer_name?: string;
      policy_number?: string;
      producer_code?: string;
      branch_code?: string;
      message_guid?: string;
      business_type?: string;
      business_purpose?: string[];
    }
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      dispatch(fetchNoticesListProgress());

      let finalUrl = `/general/get-notice-and-edocs?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&columnList=notice_and_edoc_code&columnList=record_type&columnList=status&columnList=message_guid&columnList=customer_name&columnList=policy_number&columnList=type&columnList=customer_email&columnList=business_purpose_type&columnList=insurance_company&columnList=line_of_business&columnList=producer_code&columnList=branch_code&columnList=create_ts&columnList=insert_ts&columnList=due_date&columnList=notice_type&columnList=message_type&columnList=attachment&columnList=customer_id&columnList=customer_policy_id&columnList=due_time&columnList=assigned_to_id&columnList=action_taken&columnList=mobile_number&columnList=description&columnList=financed&columnList=financed_by&columnList=contract_term_effective_date&columnList=contract_term_expiry_date&columnList=action_needed&columnList=action_status&columnList=remarks&columnList=created_by_name&columnList=modified_by_name&columnList=producer_one_id`;

      if (status !== "-1") {
        finalUrl = finalUrl + "&status=" + status;
      }
      if (type !== "-1" && type !== undefined) {
        finalUrl = finalUrl + "&column=record_type&value=" + type;
      }
      const advanceFilters: any[] = [];

      // 1️⃣ From searchValue
      if (searchValue.length > 0) {
        // assuming searchValue is already an array of filters
        advanceFilters.push(...searchValue);
      }

      // 2️⃣ From edocType
      if (edocType) {
        advanceFilters.push({
          column: ["type"],
          operator: "EQUAL",
          value: edocType,
          logicalOperator: "AND",
        });
      }

      // 3️⃣ From action_needed
      if (actionState?.action_needed && actionState.action_needed !== "-1") {
        advanceFilters.push({
          column: ["action_needed"],
          operator: "EQUAL",
          value: actionState.action_needed,
          logicalOperator: "AND",
        });
      }

      // 4️⃣ From action_status
      if (actionState?.action_status && actionState.action_status !== "-1") {
        advanceFilters.push({
          column: ["action_status"],
          operator: "EQUAL",
          value: actionState.action_status,
          logicalOperator: "AND",
        });
      }

      // 5️⃣ From otherFilters (top bar filters)
      if (otherFilters?.customer_name) {
        advanceFilters.push({
          column: ["customer_name"],
          operator: "CONTAINS",
          value: otherFilters.customer_name,
          logicalOperator: "AND",
        });
      }
      if (otherFilters?.policy_number) {
        advanceFilters.push({
          column: ["policy_number"],
          operator: "EQUAL",
          value: otherFilters.policy_number,
          logicalOperator: "AND",
        });
      }
      if (otherFilters?.producer_code) {
        advanceFilters.push({
          column: ["producer_code"],
          operator: "EQUAL",
          value: otherFilters.producer_code,
          logicalOperator: "AND",
        });
      }
      if (otherFilters?.branch_code) {
        advanceFilters.push({
          column: ["branch_code"],
          operator: "EQUAL",
          value: otherFilters.branch_code,
          logicalOperator: "AND",
        });
      }
      if (otherFilters?.message_guid) {
        advanceFilters.push({
          column: ["message_guid"],
          operator: "EQUAL",
          value: otherFilters.message_guid,
          logicalOperator: "AND",
        });
      }
      if (otherFilters?.business_type) {
        advanceFilters.push({
          column: ["type"],
          operator: "EQUAL",
          value: otherFilters.business_type,
          logicalOperator: "AND",
        });
      }
      if (
        otherFilters?.business_purpose &&
        otherFilters.business_purpose.length > 0
      ) {
        advanceFilters.push({
          // NOTICE records store the business purpose in notice_type while
          // EDOC records store it in business_purpose_type, so match both.
          column: ["business_purpose_type", "notice_type"],
          operator: "IN",
          value: otherFilters.business_purpose,
          logicalOperator: "AND",
        });
      }

      // 6️⃣ Finally append ONCE
      if (advanceFilters.length > 0) {
        finalUrl += `&advanceFilter=${encodeURIComponent(
          JSON.stringify(advanceFilters)
        )}`;
      }
      const res = await api.get(finalUrl);
      const data: INotices[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchNoticesListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchNoticesListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertNoticesAysnc =
  (
    notices: INotices,
    initialData: INotices,
    isUpdate: boolean,
    file: any | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {};
      const path = await uploadFile(
        file,
        "NOTICE",
        notices.attachment || "",
        asPayload
      );
      console.log("notices ==>", notices)
      await makeApiCall(
        {
          url: "/other/upsert-notice",
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "notice_code",
          },
        },
        initialData,
        {
          incremental_number: null,
          notice_code: notices.notice_and_edoc_code,
          notice_type: notices.notice_type,
          description: notices.description,
          customer_id: notices.customer_id,
          customer_policy_id: notices.customer_policy_id,
          customer_name: notices.customer_name,
          policy_number: notices.policy_number,
          line_of_business: notices.line_of_business,
          producer_code: notices.producer_code,
          csr_code: notices.csr_code,
          branch_code: notices.branch_code,
          status_other: notices.status_other,
          action_taken: notices.action_taken,
          attachment: path,
          assigned_to_id: notices.assigned_to_id,
          status: notices.status,
          message_type: notices.message_type,
          mobile_number: notices.mobile_number,
          email: notices.email,
          due_date: notices.due_date,
          due_time: notices.due_time,
          policy_type: notices.policy_type,
          insurer_id: notices.insurer_id,
          insurance_company: notices.insurance_company,
          producer_one_id: notices.producer_one_id
        }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Notice saved succesfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertNoticeAndEdocAsync =
  (
    data: INotices,
    initialData: INotices,
    recordType: "NOTICE" | "EDOC",
    isUpdate: boolean,
    file: any | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        policy_number: data.policy_number,
      };
      const path = await uploadFile(
        file,
        recordType === "EDOC" ? "CSIO" : "NOTICE",
        data.attachment || "",
        asPayload
      );

      const payload = {
        notice_and_edocs_id: data.notice_and_edocs_id || null,
        record_type: recordType,
        notice_and_edoc_code: data.notice_and_edoc_code,
        customer_policy_id: data.customer_policy_id,
        customer_id: data.customer_id,
        customer_name: data.customer_name,
        customer_email: data.customer_email,
        insurer_id: data.insurer_id,
        policy_type: data.policy_type,
        notice_type: data.notice_type,
        description: data.description,
        policy_number: data.policy_number,
        line_of_business: data.line_of_business,
        producer_code: data.producer_code,
        csr_code: data.csr_code,
        branch_code: data.branch_code,
        status_other: data.status_other,
        action_taken: data.action_taken,
        due_time: data.due_time,
        due_date: data.due_date,
        message_type: data.message_type,
        email: data.email,
        mobile_number: data.mobile_number,
        assigned_to_id: data.assigned_to_id,
        message_guid: data.message_guid,
        action_status: data.action_status,
        action_needed: data.action_needed,
        type: data.type,
        financed: data.financed,
        financed_by: data.financed_by,
        business_purpose_type: data.business_purpose_type,
        insurance_company: data.insurance_company,
        contract_term_effective_date: data.contract_term_effective_date,
        contract_term_expiry_date: data.contract_term_expiry_date,
        attachment: path,
        remarks: data.remarks,
        producer_one_id: data.producer_one_id,
        status: data.status,
      };

      await makeApiCall(
        {
          url: "/general/upsert-notice-and-edoc",
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "notice_and_edoc_code",
          },
        },
        initialData,
        payload
      );
      dispatch(
        showMessage({
          type: "success",
          message:
            recordType === "EDOC"
              ? "eDoc saved successfully!"
              : "Notice saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const updateNoticesEdocsStatus =
  (
    keys: string[],
    status: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      await api.put(`/general/update-notice-and-edocs-status`, {
        primary_key: keys,
        status: status,
      });
      dispatch(
        showMessage({
          displayAs: "snackbar",
          message: "eDocs/Notices reviewed successfully.",
          type: "success",
        })
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const downloadNoticesEdocsPDFAsync =
  (
    notice_and_edoc_code: string,
    onCallback: (data: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/accounting/preview-account-invoice-pdf`, {
        notice_and_edoc_code,
      });
      const data = res.data.data;
      onCallback(data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const previewNoticesEdocsPDFAsync =
  (
    notice_and_edoc_code: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/accounting/preview-account-invoice-pdf`, {
        notice_and_edoc_code,
      });

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchAllCustomerDetailsForNotices =
  (
    pageNumber: number,
    rowsInPerPage: number,
    searchvalue: string,
    onCallback: (data: ICustomerAllPolicyDetail[], totalRecords: number) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const url = `/policy/get-all-customer-policy-details?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&column=customer_name&column=customer_code&column=policy_number&value=${searchvalue}&status=ACTIVE`;

      const res = await api.get(url);
      const data: ICustomerAllPolicyDetail[] = res.data.data;
      onCallback(data, res.data.totalRecords);
    } catch (err: any) {
      onCallback([], 0);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_NOTICES_STATE = "CLEAR_NOTICES_STATE";

export const clearNoticesState = () => action(CLEAR_NOTICES_STATE);
